<template>
  <div v-if="formResponse" class="booking-summary booking-details">

    <!--  Before filling in form  -->
    <div v-if="!formResponse.completed && !submitted">

      <!--  Overview details  -->
      <table v-if="formResponse.showBookingDetails" class="booking-summary__summary">
        <thead>
        <th colspan="2"><h4>Booking Summary</h4></th>
        </thead>
        <tr>
          <td>Booked by:</td>
          <td>{{formResponse.booking.bookedBy.firstName}} {{formResponse.booking.bookedBy.lastName}}</td>
        </tr>
        <tr>
          <td>Check-in:</td>
          <td>
            {{formatDate(formResponse.booking.arrival)}}
            <template v-if="formResponse.booking.initialArrival && formResponse.booking.arrival !== formResponse.booking.initialArrival">
              <span class="booking-summary__summary--amended">{{formatDate(formResponse.booking.initialArrival)}}</span>
            </template>
          </td>
        </tr>
        <tr>
          <td>Check-out:</td>
          <td>
            {{formatDate(formResponse.booking.departure)}}
            <template v-if="formResponse.booking.initialDeparture && formResponse.booking.departure !== formResponse.booking.initialDeparture">
              <span class="booking-summary__summary--amended">{{formatDate(formResponse.booking.initialDeparture)}}</span>
            </template>
          </td>
        </tr>
        <tr>
          <td>Nights:</td>
          <td>
            {{numberOfNights}}
            <template v-if="initialNumberOfNights && numberOfNights !== initialNumberOfNights">
              <span class="booking-summary__summary--amended">{{initialNumberOfNights}}</span>
            </template>
          </td>
        </tr>
        <tr>
          <td>Persons:</td>
          <td>
            {{totalPersons}}
            <template v-if="initialTotalPersons && totalPersons !== initialTotalPersons">
              <span class="booking-summary__summary--amended">{{initialTotalPersons}}</span>
            </template>
          </td>
        </tr>
        <tr v-if="formResponse.showPricing && formResponse.booking.total !== '0.00'">
          <td>Total:</td>
          <td>
            <template v-if="isAmendingBooking">
              Pending confirmation from host
            </template>
            <template v-else>
              {{formResponse.booking.currencyCode}} {{Number(formResponse.booking.total).toLocaleString()}}
              <template v-if="formResponse.booking.initialTotal && formResponse.booking.initialTotal > 0 && formResponse.booking.total !== formResponse.booking.initialTotal">
                <span class="booking-summary__summary--amended">{{Number(formResponse.booking.initialTotal).toLocaleString()}}</span>
              </template>
            </template>
          </td>
        </tr>
        <template v-if="(cleaningFee > 0) || (breakageDeposit > 0 && !isAirBnbBooking) || (formResponse.showPricing && formResponse.booking.total !== '0.00')">
          <tr>
            <td>Deposits:</td>
            <td>
              <template v-if="formResponse.showPricing && formResponse.booking.total !== '0.00'">
                <template v-if="isAmendingBooking">
                  Pending confirmation from host
                </template>
                <template v-else>
                  {{formResponse.booking.currencyCode}} {{Number(formResponse.booking.deposit).toLocaleString()}}
                  <template v-if="formResponse.booking.initialDeposit && formResponse.booking.initialDeposit > 0 && formResponse.booking.deposit !== formResponse.booking.initialDeposit">
                    <span class="booking-summary__summary--amended">{{Number(formResponse.booking.initialDeposit).toLocaleString()}}</span>
                  </template>
                  (booking)
                </template>
              </template>
              <template v-if="cleaningFee > 0">
                <br/>
                {{formResponse.booking.currencyCode}} {{Number(cleaningFee).toLocaleString()}} (cleaning fee)
              </template>
              <!-- Note: Don't show this for AirBnb bookings as they take all deposits -->
              <template v-if="breakageDeposit > 0 && !isAirBnbBooking">
                <br/>
                {{formResponse.booking.currencyCode}} {{Number(breakageDeposit).toLocaleString()}}
                ({{getUnitTypeForDisplay(formResponse.establishment.preferences.displayCategoryForRoomTypes)}} security, refundable)
              </template>
            </td>
          </tr>
        </template>

        <tr><td class="booking-summary__summary-section-break"></td></tr>

        <template v-for="(room, roomIndex) in formResponse.booking.rooms">
          <tr v-bind:key="'room' + roomIndex">
            <td>
              <b>
                <template v-if="formResponse.establishment.preferences.showRoomTypeNameInsteadOfRoomName">
                  <template v-if="room.roomTypeName">
                    {{room.roomTypeName}}
                  </template>
                  <template v-else>
                    Room {{roomIndex+1}}
                  </template>
                </template>
                <template v-else>
                  <template v-if="room.roomName">
                    {{room.roomName}}
                  </template>
                  <template v-else>
                    Room {{roomIndex+1}}
                  </template>
                </template>
              </b>
            </td>
            <td></td>
          </tr>
          <tr v-for="(adult, index) in room.adults" v-bind:key="'adult' + index">
            <td class="booking-summary__summary-guest-type">
              Adult {{index+1}}
            </td>
            <td class="booking-summary__summary-guests">
              <template v-if="adult.lastName">
                {{adult.firstName}} {{adult.lastName}}<br>
              </template>
              <template v-else>
                Name unknown<br>
              </template>
              <span v-if="index < 2">
                <i class="fas fa-envelope"></i>
                <template v-if="adult.email">
                  {{adult.email}}
                </template>
                <template v-else>
                  Not supplied
                </template>
                <br>
              </span>
              <span v-if="index < 2">
                <i data-v-681a9210="" class="fas fa-phone"></i>
                <template v-if="adult.mobile">
                  {{adult.mobile}}
                </template>
                <template v-else>
                  Not supplied
                </template>
              </span>
            </td>
          </tr>
          <tr v-for="(child, childIndex) in getAllChildrenForRoom(room)" v-bind:key="'child' + childIndex">
            <td class="booking-summary__summary-guest-type">
              Child {{childIndex+1}}
            </td>
            <td class="booking-summary__summary-guests">
              <template v-if="child.lastName">
                {{child.firstName}} {{child.lastName}}<br>
              </template>
              <template v-else>
                Name unknown<br>
              </template>
            </td>
          </tr>
        </template>
      </table>

    </div>

    <!-- Submitted form-->
    <div v-else-if="!formResponse.completed && submitted" class="booking-summary__thank-you">
      <p>
        Thank you, please be patient while we save your response.
      </p>
      <div class="check-in__loading">
        <Spinner colour="#242F3E"/>
      </div>
    </div>

    <!-- After form submitted -->
    <div v-else class="booking-summary__thank-you">
      Thank you for completing your form. A copy has been emailed to you.
    </div>

  </div>
</template>

<script>
import Spinner from '../../components/Spinner.vue'

import { bookingMixin } from '../../mixins/BookingMixin'

export default {
  name: 'BookingDetails',
  components: { Spinner },
  mixins: [bookingMixin],
  props: {
    formResponse: Object
  },
  data: function () {
    return {
    }
  },
  computed: {
  },
  methods: {
  },
  mounted () {
  }
}
</script>

<style lang="scss" scoped>
  @import "../../styles/base/base.scss";
  @import "../../styles/components/booking_summary";
  @import "../../styles/components/booking_details";
</style>
